import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const DistrictCommitteeAddForm = () => {
  const [form, setForm] = useState({
    district: "",
    name: "",
    designation: "",
    image: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");

  const districts = [
    "Srikakulam",
    "Vizianagram",
    "Visakhapatnam",
    "Kakinada",
    "East Godavari",
    "Konaseema",
    "West Godavari",
    "Eluru",
    "Vijayawada",
    "Krishna",
    "Guntur",
    "Palnadu",
    "Bapatla",
    "Prakasam",
    "Nellore",
    "Nandayala",
    "Kurnool",
    "Anantapur",
    "SriSathyasai",
    "Kadapa",
    "Annamayya",
    "TIRUPATI",
    "CHITTOOR"
  ];

  const designations = ["President", "Secretary", "Treasurer"];

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setForm({ ...form, image: reader.result });
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.district || !form.name || !form.designation || !form.image) {
      setUploadStatus("❌ Please fill all fields");
      setTimeout(() => setUploadStatus(""), 3000);
      return;
    }

    setLoading(true);
    setUploadStatus("");

    try {
      await axios.post(
        `${process.env.REACT_APP_REQUESTING_API}/api/district-committee`,
        form
      );
      setUploadStatus("✅ Member added successfully!");
      setForm({ district: "", name: "", designation: "", image: "" });
      setTimeout(() => setUploadStatus(""), 5000);
    } catch (error) {
      console.error("Error adding member:", error);
      setUploadStatus("❌ Failed to add member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 mb-3">
            Add Committee Member
          </h1>
          <p className="text-gray-600 text-lg">
            Add a new district committee member
          </p>
        </motion.div>

        {/* Upload Status Message */}
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl font-semibold text-center ${
              uploadStatus.includes("✅")
                ? "bg-green-100 text-green-800 border-2 border-green-300"
                : "bg-red-100 text-red-800 border-2 border-red-300"
            }`}
          >
            {uploadStatus}
          </motion.div>
        )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200"
          >
            <div className="space-y-6">
              {/* District Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  District *
                </label>
                <select
                  name="district"
                  value={form.district}
                  onChange={handleChange}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                  required
                >
                  <option value="">-- Select District --</option>
                  {districts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>

              {/* Two Column Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Member Name */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Member Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter full name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    required
                  />
                </div>

                {/* Designation */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Designation *
                  </label>
                  <select
                    name="designation"
                    value={form.designation}
                    onChange={handleChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">-- Select Designation --</option>
                    {designations.map((designation) => (
                      <option key={designation} value={designation}>
                        {designation}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Upload Member Photo *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white file:font-semibold hover:file:bg-gray-900 cursor-pointer"
                  required
                />
                {form.image && (
                  <div className="mt-4 flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <img
                      src={form.image}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg shadow-md border-2 border-gray-200"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-700">✓ Image loaded successfully</p>
                      <p className="text-xs text-gray-500 mt-1">Preview of member photo</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Adding Member...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                    Add Member
                  </span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default DistrictCommitteeAddForm;

// working 16/12/25

// import React, { useState } from "react";
// import axios from "axios";

// const DistrictCommitteeAddForm = () => {
//   const [form, setForm] = useState({
//     district: "",
//     name: "",
//     designation: "",
//     image: "",
//   });

//   const districts = [
//     "Srikakulam",
//     "Vizianagram",
//     "Visakhapatnam",
//     "Kakinada",
//     "East Godavari",
//     "Konaseema",
//     "West Godavari",
//     "Eluru",
//     "Vijayawada",
//     "Krishna",
//     "Guntur",
//     "Palnadu",
//     "Bapatla",
//     "Prakasam",
//     "Nellore",
//     "Nandayala",
//     "Kurnool",
//     "Anantapur",
//     "SriSathyasai",
//     "Kadapa",
//     "Annamayya",
//     "TIRUPATI",
//     "CHITTOOR"
//   ];
  
//   const designations = ["President", "Secretary", "Treasurer"];

//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };
  
//   const handleImageChange = (e) => {
//     const file = e.target.files[0];
//     const reader = new FileReader();
//     reader.readAsDataURL(file);
//     reader.onloadend = () => {
//       setForm({ ...form, image: reader.result });
//     };
//   };
  
//   const handleSubmit = async (e) => {
//     e.preventDefault();
  
//     if (!form.district || !form.name || !form.designation || !form.image) {
//       alert("Please fill all fields");
//       return;
//     }
  
//     try {
//       await axios.post(
//         `${process.env.REACT_APP_REQUESTING_API}/api/district-committee`,form );
//       alert("Member added successfully!");
//       setForm({ district: "", name: "", designation: "", image: "" });
//     } catch (error) {
//       console.error("Error adding member:", error);
//       alert("Failed to add member");
//     }
//   };
  

//   return (
//     <div className="max-w-2xl mx-auto p-4">
//       <h2 className="text-2xl font-bold mb-4">Add District Committee Member</h2>
//       <form onSubmit={handleSubmit} className="space-y-4">

//         <select
//           name="district"
//           value={form.district}
//           onChange={handleChange}
//           className="w-full p-2 border rounded"
//         >
//           <option value="">Select District</option>
//           {districts.map((district) => (
//             <option key={district} value={district}>
//               {district}
//             </option>
//           ))}
//         </select>

//         <input
//           type="text"
//           name="name"
//           placeholder="Enter Name"
//           value={form.name}
//           onChange={handleChange}
//           className="w-full p-2 border rounded"
//         />

//         <select
//           name="designation"
//           value={form.designation}
//           onChange={handleChange}
//           className="w-full p-2 border rounded"
//         >
//           <option value="">Select Designation</option>
//           {designations.map((designation) => (
//             <option key={designation} value={designation}>
//               {designation}
//             </option>
//           ))}
//         </select>

//         <input
//           type="file"
//           accept="image/*"
//           onChange={handleImageChange}
//           className="w-full p-2 border rounded"
//         />

//         {form.image && (
//           <img
//             src={form.image}
//             alt="Preview"
//             className="w-32 h-32 object-cover mt-2 rounded"
//           />
//         )}

//         <button
//           type="submit"
//           className="bg-blue-500 text-white px-4 py-2 rounded"
//         >
//           Add Member
//         </button>
//       </form>
//     </div>
//   );
// };

// export default DistrictCommitteeAddForm;
